import { useDocItems } from '@modules/docs/providers';
import { Link, Stack, Typography, styled } from '@mui/material';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { Doc } from 'src/@types/docs';
import { routes } from 'src/Router';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { CONTAINER_PADDING } from './constants';

const FooterContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2, CONTAINER_PADDING),
  marginTop: theme.spacing(4),
  a: {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.text.primary,
      textDecoration: 'none',
    },
  },
}));

const FooterTitleContainer = styled(Stack)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 0),
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type Props = {
  doc: Doc;
};

export const DocFooter = ({ doc }: Props) => {
  const { docs } = useDocItems();
  const { translate } = useLocales();

  const docChildrens = useMemo(() => {
    const currentDoc = docs?.find((d) => d._id === doc._id);
    if (!docs?.length || !currentDoc) return [];

    return docs.filter((item) => item.parent === currentDoc._id);
  }, [docs, doc]);

  if (!docChildrens?.length) return null;

  return (
    <FooterContainer>
      <FooterTitleContainer>
        <Typography variant="button">
          {translate('collaborate.docs.sub_documents')}
        </Typography>
      </FooterTitleContainer>
      <Stack gap={1} mt={2}>
        {docChildrens.map((doc) => (
          <Link
            key={doc._id}
            component={RouterLink}
            to={routes.dashboard.docs.view(doc._id)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {doc.icon ? (
              <em-emoji shortcodes={doc.icon?.value} />
            ) : (
              <Iconify icon="tabler:file-text" />
            )}
            {doc.title}
          </Link>
        ))}
      </Stack>
    </FooterContainer>
  );
};
